import { FederatedPointerEvent, PointData, Sprite } from 'pixi.js';
import { Subject } from 'rxjs';
import { Mouse } from './mouse';
import { Gfx } from './gfx';
import { IRoomSprite, L } from 'src/sync/_api';
import { Store } from 'src/sync/store';
import { Room } from 'src/sync/room';
import { View } from './view';
import { Settings } from './settings';

export abstract class RoomSprite {
    public moved = new Subject<void>();
    public abstract type: 'token' | 'image';
    private throttlePause = false;
    private ctrlKey = false;

    constructor(public data: IRoomSprite, public sprite: Sprite) {
        sprite.cursor = 'all-scroll';
        sprite.eventMode = 'static';
        sprite.rotation = data.rotation ?? 0;
        sprite.position.set(data.position.x, data.position.y);
        sprite.on('pointerdown', (e) => {
            this.ctrlKey = e.ctrlKey;
            this.click(e);
        });
        sprite.on('pointerup', (e) => this.ctrlKey = e.ctrlKey);
    }

    // TODO Redraw should be tied to position changes??
    public abstract redraw(): void;
    protected abstract clone(): void;
    protected abstract emitChanges(): void;

    public updatePosition(position: PointData) {
        if (this.data.locked) return;
        if (this.data.position.x == position.x && this.data.position.y == position.y) return;

        this.throttle(() => {
            // TODO: Putting this top part in a transaction should NOT break the behavior...
            if (Store.multiselect.value == null) {
                let xAdjust = this.sprite.anchor.x == 0.5 ? 0 : (this.sprite.width / 2);
                let yAdjust = this.sprite.anchor.y == 0.5 ? 0 : (this.sprite.height / 2);

                if (this.ctrlKey) {
                    this.ctrlKey = false;
                    this.clone();
                };

                this.data.position = View.bindToWorld({ x: position.x, y: position.y }, xAdjust, yAdjust);
                this.emitChanges();
                this.moved.next();
            } else {
                const controlClicked = Store.multiselect.value!.some(x => x.ctrlKey);
                Room.document.transact(() => {
                    const distance = { x: position.x - this.data.position.x, y: position.y - this.data.position.y };
                    Store.multiselect.value!.forEach(x => {
                        if (controlClicked) {
                            x.ctrlKey = false;
                            x.clone();
                        };
                        x.data.position = View.bindToWorld({ x: x.data.position.x + distance.x, y: x.data.position.y + distance.y });
                        x.emitChanges();
                        x.moved.next();
                    });
                });
            }
        }, 25);
    }

    // TODO: (noten) Unify token selection
    public select(activate: boolean) {
        if (activate) {
            Store.activeSprite.next(this);
        }
    }

    public deselect() {
        if (Store.activeSprite.value?.data.id == this.data.id) {
            Store.activeSprite.next(undefined);
        }
        this.redraw();
    }

    public destroy() {
        if (Store.activeSprite.value?.data.id == this.data.id) {
            Store.activeSprite.next(undefined);
        }
        this.sprite.destroy();
    }

    private throttle(callback: Function, time: number) {
        if (this.throttlePause) return;
        this.throttlePause = true;

        setTimeout(() => {
            callback();
            this.throttlePause = false;
        }, time);
    };

    protected click(e: FederatedPointerEvent) {
        if (e.button == 0) {
            const trashing = Mouse.erasing && Gfx.activeLayer.id == L.Tokens;
            if (trashing) return;

            // (noten): NB when snap mode is on, the 'mouse' will be snapped to the grid
            if (this.sprite.width <= Settings.gridOffset) {
                Mouse.dragOffset = { x: -(this.sprite.width / 2), y: -(this.sprite.height / 2) };
            } else {
                Mouse.dragOffset = { x: Gfx.mouse.x - this.sprite.x, y: Gfx.mouse.y - this.sprite.y };
            }

            Mouse.dragTarget = this;

            // If DragTarget is not part of the multiselect, cancel previous selection
            const multiselect = Store.multiselect.value?.map(x => x.data.id) ?? [];
            if (multiselect.indexOf(this.data.id) == -1) {
                Mouse.cancel();
            }

            this.select(false);
            Gfx.mouse.clear();
        }
    }
}
